import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fehlerText } from "@/lib/fehler";
import { Loader2, Search, Plus, Trash2, Pencil, DownloadCloud, ChevronRight, Clock, Merge, History, CalendarDays } from "lucide-react";
import { formatDateDE } from "@/lib/utils";
import KundeDetail from "@/pages/admin/KundeDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v, d = 2) => Number(v || 0).toFixed(d).replace(".", ",");
const EMPTY = {
  betrieb: "", ansprechpartner: "", email: "", rechnung_email: "", telefon: "",
  strasse: "", plz: "", ort: "", stundensatz_eur: "", ust_id: "", notiz: "", typ: "",
};

const Field = ({ label, k, form, set, type = "text", placeholder = "", span = "" }) => (
  <div className={span}>
    <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">{label}</label>
    <input
      type={type}
      value={form[k] ?? ""}
      onChange={(e) => set(k, e.target.value)}
      placeholder={placeholder}
      data-testid={`kunde-field-${k}`}
      className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm"
    />
  </div>
);

export default function KundenTab({ pwd, onCreateInvoice, onRecordHours, onOpenTab, selectedId, onSelect, onShowHistorie }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const selected = list.find((k) => k.id === selectedId) || null;
  const setSelected = (k) => onSelect?.(k ? k.id : null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/kunden/uebersicht`, { headers: { "X-Admin-Password": pwd } });
      setList(data || []);
    } catch {
      toast.error("Kundenkartei konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditId(null); setForm(EMPTY); setShowForm(true); };
  const startEdit = (k) => {
    setEditId(k.id);
    setForm({ ...EMPTY, ...k, stundensatz_eur: k.stundensatz_eur || "" });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.betrieb.trim()) { toast.error("Betrieb/Name ist erforderlich"); return; }
    setSaving(true);
    const body = {
      betrieb: form.betrieb, ansprechpartner: form.ansprechpartner, email: form.email,
      rechnung_email: form.rechnung_email, telefon: form.telefon,
      strasse: form.strasse, plz: form.plz, ort: form.ort,
      ust_id: form.ust_id, notiz: form.notiz, typ: form.typ || "",
      stundensatz_eur: Number(form.stundensatz_eur) || 0,
    };
    try {
      if (editId) {
        await axios.put(`${API}/admin/kunden/${editId}`, body, { headers: { "X-Admin-Password": pwd } });
        toast.success("Kunde aktualisiert");
      } else {
        await axios.post(`${API}/admin/kunden`, body, { headers: { "X-Admin-Password": pwd } });
        toast.success("Kunde angelegt");
      }
      setShowForm(false); setEditId(null); setForm(EMPTY);
      load();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Kunde „${k.betrieb}" aus der Kartei löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/kunden/${k.id}`, { headers: { "X-Admin-Password": pwd } });
      toast.success("Kunde gelöscht");
      if (selected?.id === k.id) setSelected(null);
      load();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    }
  };

  const dedupe = async () => {
    if (!window.confirm("Doppelte Kunden zusammenführen?\n\nGleiche Betriebe werden auf einen Datensatz zusammengelegt — der zuletzt bearbeitete gewinnt, fehlende Felder werden ergänzt.")) return;
    try {
      const { data } = await axios.post(`${API}/admin/kunden/dedupe`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success(data.removed
        ? `${data.removed} Doppel-Eintrag/Einträge zusammengeführt — ${data.total} Kunden`
        : "Keine Doppel-Einträge gefunden");
      setSelected(null);
      load();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    }
  };

  const importAll = async () => {
    if (!window.confirm("Bestehende Kunden aus Anfragen, Rechnungen und Stundennachweisen übernehmen?")) return;
    try {
      const { data } = await axios.post(`${API}/admin/kunden/import`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success(`${data.imported} neue Kunden übernommen — ${data.total} insgesamt`);
      load();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    }
  };

  if (selected && !showForm) {
    return (
      <KundeDetail
        pwd={pwd}
        kunde={selected}
        onBack={() => { setSelected(null); load(); }}
        onEdit={startEdit}
        onCreateInvoice={onCreateInvoice}
        onRecordHours={onRecordHours}
        onOpenTab={onOpenTab}
      />
    );
  }

  const term = q.trim().toLowerCase();
  const visible = term
    ? list.filter((k) => [k.betrieb, k.ansprechpartner, k.ort, k.email]
        .filter(Boolean).some((v) => v.toLowerCase().includes(term)))
    : list;
  const mietkoch = visible.filter((k) => k.typ !== "privat");
  const privat = visible.filter((k) => k.typ === "privat");

  const Karte = ({ k, schlank }) => (
    <div key={k.id} data-testid={`kunde-${k.id}`}
      className="group border border-[#E3DECE] bg-white hover:border-[#BA6D52] transition-colors">
      <button onClick={() => setSelected(k)} data-testid={`kunde-${k.id}-open`}
        className="w-full text-left p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-serif text-xl text-[#2C2A26] truncate">{k.betrieb}</p>
            <p className="text-xs text-[#757067] mt-1 truncate">
              {[k.ansprechpartner, k.ort].filter(Boolean).join(" · ") || k.email || "—"}
            </p>
          </div>
          <ChevronRight size={18} className="text-[#E3DECE] group-hover:text-[#BA6D52] shrink-0 transition-colors" />
        </div>

        {schlank ? (
          <div className="mt-4 text-xs">
            {k.naechster_einsatz ? (
              <span className="inline-flex items-center gap-1.5 text-[#BA6D52]">
                <CalendarDays size={12} /> Veranstaltung {formatDateDE(k.naechster_einsatz)}
              </span>
            ) : k.last_einsatz ? (
              <span className="text-[#757067]">zuletzt {formatDateDE(k.last_einsatz)}</span>
            ) : (
              <span className="text-[#9A958C] italic">noch kein Termin hinterlegt</span>
            )}
            {k.umsatz_netto_eur > 0 && (
              <span className="text-[#757067]"> · {num(k.umsatz_netto_eur)} € netto gesamt</span>
            )}
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs">
              {k.hours_open > 0 ? (
                <span className="inline-flex items-center gap-1.5 text-[#BA6D52]">
                  <Clock size={12} /> {num(k.hours_open)} Std. offen · {num(k.net_open_eur)} € netto
                </span>
              ) : (
                <span className="text-[#757067]">Keine offenen Stunden</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#757067]">
              <span>{k.shift_count} Einsätze · {k.invoice_count} Rechnungen</span>
              {k.umsatz_netto_eur > 0 && <span>{num(k.umsatz_netto_eur)} € netto gesamt</span>}
              {k.stundensatz_eur > 0 && <span>{num(k.stundensatz_eur)} €/h</span>}
              {k.last_einsatz && <span>zuletzt {formatDateDE(k.last_einsatz)}</span>}
            </div>
          </>
        )}
      </button>
      <div className="flex justify-end gap-2 px-5 pb-4">
        <button onClick={() => startEdit(k)} data-testid={`kunde-${k.id}-edit`} title="Bearbeiten"
          className="border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] p-2 transition-colors">
          <Pencil size={12} />
        </button>
        <button onClick={() => remove(k)} data-testid={`kunde-${k.id}-delete`} title="Löschen"
          className="border border-red-200 text-red-700 hover:bg-red-50 p-2 transition-colors">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="kunden-tab" className="space-y-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#757067]" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Kunden suchen …" data-testid="kunden-search"
            className="w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] pl-9 pr-3 py-2.5 text-sm"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onShowHistorie} data-testid="kunden-historie"
            title="Kunden aus früheren Website-Anfragen"
            className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
            <History size={14} /> Aus Anfragen
          </button>
          <button onClick={importAll} data-testid="kunden-import"
            className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
            <DownloadCloud size={14} /> Bestehende übernehmen
          </button>
          <button onClick={dedupe} data-testid="kunden-dedupe"
            title="Gleiche Betriebe auf einen Datensatz zusammenlegen"
            className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
            <Merge size={14} /> Doppelte zusammenführen
          </button>
          <button onClick={startNew} data-testid="kunde-new"
            className="inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-5 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
            <Plus size={14} /> Neuer Kunde
          </button>
        </div>
      </div>

      {showForm && (
        <div className="border border-[#BA6D52] bg-[#FAF5F1] p-5 md:p-6" data-testid="kunde-form">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">
            {editId ? "Kunde bearbeiten" : "Neuer Kunde"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Betrieb / Name *" k="betrieb" form={form} set={set} placeholder="z. B. Hotel Zentlinde GbR" span="md:col-span-2" />
            <Field label="Ansprechpartner" k="ansprechpartner" form={form} set={set} placeholder="Küchenchef" />
            <Field label="E-Mail" k="email" form={form} set={set} type="email" />
            <Field label="Rechnungs-E-Mail (Buchhaltung)" k="rechnung_email" form={form} set={set}
              type="email" placeholder="buchhaltung@…" span="md:col-span-2" />
            <Field label="Telefon / WhatsApp" k="telefon" form={form} set={set} type="tel" />
            <Field label="Stundensatz netto €" k="stundensatz_eur" form={form} set={set} type="number" placeholder="45" />
            <Field label="Straße & Nr." k="strasse" form={form} set={set} span="md:col-span-2" />
            <Field label="PLZ" k="plz" form={form} set={set} />
            <Field label="Ort" k="ort" form={form} set={set} />
            <Field label="USt-IdNr. (optional)" k="ust_id" form={form} set={set} />
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Art des Kunden</label>
              <select value={form.typ ?? ""} onChange={(e) => set("typ", e.target.value)}
                data-testid="kunde-field-typ"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm">
                <option value="">Automatisch erkennen</option>
                <option value="mietkoch">Mietkoch — Betrieb</option>
                <option value="privat">Privatkoch — Privatkunde</option>
              </select>
            </div>
            <Field label="Notiz / Vereinbarungen" k="notiz" form={form} set={set} placeholder="z. B. Abrechnung monatlich, Parken im Hof" />
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={save} disabled={saving} data-testid="kunde-save"
              className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Speichert …</> : "Speichern"}
            </button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} data-testid="kunde-cancel"
              className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.15em]">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-[#757067]">Lade …</p>}
      {!loading && visible.length === 0 && (
        <div className="py-14 text-center border border-dashed border-[#E3DECE]">
          <p className="text-sm text-[#757067] italic">
            {list.length === 0
              ? "Noch keine Kunden — lege einen an oder übernimm die bestehenden aus Anfragen und Rechnungen."
              : "Kein Kunde gefunden."}
          </p>
        </div>
      )}

      <div className="space-y-8">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            Mietkoch · Betriebe <span className="text-[#9A958C]">({mietkoch.length})</span>
          </p>
          {mietkoch.length === 0 ? (
            <p className="text-sm text-[#9A958C] italic mt-3">Noch keine Betriebe in dieser Liste.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3"
              data-testid="kunden-mietkoch">
              {mietkoch.map((k) => <Karte key={k.id} k={k} />)}
            </div>
          )}
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            Privatkoch · Privatkunden <span className="text-[#9A958C]">({privat.length})</span>
          </p>
          {privat.length === 0 ? (
            <p className="text-sm text-[#9A958C] italic mt-3">Noch keine Privatkunden in dieser Liste.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3"
              data-testid="kunden-privat">
              {privat.map((k) => <Karte key={k.id} k={k} schlank />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
